/**
 * Small inline badges.
 *
 * Three related pills the prototype defined separately; grouped here because they
 * share the rounded-999 / small-mono-text treatment. Styles are verbatim.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Info, RefreshCw } from 'lucide-react';
import { c } from '../../styles/theme';

export interface ResetBadgeProps {
  label?: string;
}

/** "Resets monthly" indicator beside the credit meter. */
export function ResetBadge({ label = 'Resets monthly' }: ResetBadgeProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: c.text2,
        background: c.surface3,
        border: `1px solid ${c.border}`,
        borderRadius: 999,
        padding: '2px 8px',
        fontFamily: 'IBM Plex Mono',
      }}
    >
      <RefreshCw size={10} />
      {label}
    </span>
  );
}

export interface PreviewTagProps {
  label?: string;
}

/**
 * Marks a control that does not yet do the real thing.
 *
 * Part of the Phase 1 honest-labelling work: several flows present themselves as
 * finished ("MP3", "WAV", "Download everything (ZIP)") while producing a
 * placeholder text file. Removed once Phase 5 lands real audio.
 */
export function PreviewTag({ label = 'Preview' }: PreviewTagProps) {
  return (
    <span
      style={{
        fontSize: 9,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: c.text2,
        background: c.surface3,
        border: `1px solid ${c.border}`,
        borderRadius: 999,
        padding: '1px 6px',
        fontWeight: 500,
      }}
    >
      {label}
    </span>
  );
}

export interface PrototypeNoticeProps {
  children: ReactNode;
  style?: CSSProperties;
}

/**
 * Inline explanatory notice for a panel whose output is simulated.
 *
 * THE BOX METRICS LIVE IN CSS (.lyx-prototype-notice), not in the style prop below.
 * This is the longest single piece of prose in the Studio — the Mix panel's notice
 * concatenates two AUDIO_EDIT_CAPABILITIES `missing` strings and the Upload panel's
 * joins three blocker summaries — and every one of those strings is written for a
 * desktop column. With `padding`, `font-size`, `gap` and `line-height` inline there
 * was no way for a breakpoint to reach them, so a 380px panel spent 22px of its width
 * on the notice's own padding and rendered ~28 characters a line; the notice then ran
 * taller than the panel and sat over the controls beneath it. Same rule as
 * .lyx-tool-panel and .lyx-tool-slider: an inline style prop outranks a media query,
 * so anything a breakpoint has to change cannot be inline.
 *
 * Only the theme colours stay inline, because no breakpoint changes them, and `style`
 * is still spread last so a caller's `marginBottom`/`marginTop` keeps working.
 */
export function PrototypeNotice({ children, style }: PrototypeNoticeProps) {
  return (
    <div
      className="lyx-prototype-notice"
      style={{
        border: `1px solid ${c.border}`,
        background: c.surface2,
        color: c.text2,
        ...style,
      }}
    >
      <Info size={12} color="currentColor" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
      {/*
        `min-width: 0`, without which the wrapping below does nothing. This span is a
        flex item, and a flex item's automatic minimum size is its min-content width —
        for a capability string that is its longest unbreakable word ("phase-vocoder",
        a filename, a hyphen-free clause), so the span refused to shrink past it and
        widened the notice, and with it the panel, past the centre column's edge where
        .lyx-ws-body's `overflow: hidden` cuts it off. `overflow-wrap: anywhere` is the
        second half: .lyx-tool-panel already carries it, but a caller outside a tool
        panel (the Dashboard, Legal, Share) does not inherit it.
      */}
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}
